import { Category } from '@wix/answers-api/dist/kb';
import { Box, Heading, Pagination } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { FunctionComponent, useContext } from 'react';
import { animateScroll } from 'react-scroll';
import {
  GEN_ANSWER_RELATED_ARTICLES_COUNT,
} from '../../constants';
import { DATA_HOOKS } from '../../dataHooks';
import { SEARCH_RESULTS_PAGE_SIZE } from '../../pages/search';
import {
  ArticleSearchResult,
} from '../../types';
import { Breadcrumbs } from '../Breadcrumbs';
import { RenderHTML } from '../RenderHTML';
import { isMobile } from '../../utils/userAgent';
import { Context } from '../../context';
import { GeneratedAnswer } from './GeneratedAnswer';
import {
  ItemOnPageEventSource,
  useBiOnVisibleItem,
} from '../../hooks/useBiOnVisibleItem';
import classNames from 'classnames';
import { SkeletonLoading } from './SkeletonLoading';
import css from './index.module.scss';
import { SearchResultItems } from './SearchResultsItems';
import { useIsGenAnswer } from '../../hooks/useIsGenAnswer';

export type SearchResultsProps = {
  term: string;
  searchResults: ArticleSearchResult;
  categoriesPaths: Record<string, Category[]>;
  currentPage: number;
  onPageChange: (page: number) => void;
  isLoading: boolean;
  generatedAnswer: string | null;
  relatedArticlesTitles?: string[];
  isProhibited: boolean | null;
  retryGenerateAnswer: () => Promise<void>;
};

export const SearchResults: FunctionComponent<SearchResultsProps> = ({
  term,
  searchResults,
  categoriesPaths,
  currentPage,
  onPageChange,
  isLoading,
  generatedAnswer,
  relatedArticlesTitles,
  isProhibited,
  retryGenerateAnswer,
}) => {
  const { t } = useTranslation();
  const { itemsCount } = searchResults;
  const context = useContext(Context);
  const isMobileView = isMobile(context);
  const { itemRef } = useBiOnVisibleItem(
    ItemOnPageEventSource.MORE_SEARCH_RESULTS
  );

  const isNextGenSearchEnabled = useIsGenAnswer();
  const displayGeneratedAnswer = isNextGenSearchEnabled && currentPage === 1;
  const displayGeneratedAnswerLoading =
    isNextGenSearchEnabled && generatedAnswer === null && currentPage === 1;

  const onPageChangeEvent = async (page: number) => {
    animateScroll.scrollToTop();
    onPageChange(page);
  };

  const numPages = Math.ceil(itemsCount / SEARCH_RESULTS_PAGE_SIZE);

  const breadcrumbItems = [{ id: 'search-results', label: t('search-results-page.breadcrumb') }];

  const resultsTitle =
    itemsCount >= 0
      ? t('search-results-page.title', { count: itemsCount, term })
      : t('search-results-page.title-single', { term });

  const genAnswerResultsTitleGrey = t('search-results-page.title-answer-gen-more');
  const genAnswerResultsTitleBlack = ` ${t('search-results-page.title-answer-gen-term', { term })}`;
  const topArticles = searchResults.items.slice(0, GEN_ANSWER_RELATED_ARTICLES_COUNT);
  const relatedArticles = topArticles.filter((article) => relatedArticlesTitles?.includes(article.title));

  return (
    <Box
      direction="vertical"
      className={css.searchResultsWrapper}
      dataHook={DATA_HOOKS.SEARCH_RESULTS}
    >
      <Breadcrumbs breadcrumbItems={breadcrumbItems} shallow={false} />
      {displayGeneratedAnswer && (
        <GeneratedAnswer
          searchQuestion={term}
          searchAnswer={generatedAnswer}
          articleLinks={relatedArticles}
          isProhibited={isProhibited}
          retryGenerateAnswer={retryGenerateAnswer}
        />
      )}
      <Box
        direction="vertical"
        className={classNames(css.searchResults, {
          [css.includeGeneratedAnswer]: displayGeneratedAnswer,
        })}
      >
        <Box
          className={classNames(css.searchResultsHeader, {
            [css.nextGenSearchDisabled]: !isNextGenSearchEnabled,
          })}
          verticalAlign="middle"
          align="space-between"
        >
          {!displayGeneratedAnswerLoading && (
            <Heading
              appearance={!displayGeneratedAnswer ? 'H4' : 'H3'}
              ellipsis
              dataHook={DATA_HOOKS.SEARCH_RESULTS_TITLE}
              className={css.searchResultsTitle}
            >
              {!displayGeneratedAnswer ? (
                <RenderHTML
                  html={resultsTitle}
                  className={css.searchResultsTitleHTML}
                  textElement={
                    isMobileView
                      ? {
                        ellipsis: true,
                        maxLines: 3,
                      }
                      : undefined
                  }
                />
              ) : (
                <Box
                  dataHook={DATA_HOOKS.MORE_RESULTS}
                  className={classNames(css.searchResultsTitleHTML, {
                    [css.generatedAnswerWide]: displayGeneratedAnswer,
                  })}
                  ref={itemRef}
                  direction="horizontal"
                >
                  <RenderHTML
                    className={css.searchResultsTitleGrey}
                    html={genAnswerResultsTitleGrey}
                    headingElement={{ size: 'large' }}
                  />
                  <RenderHTML
                    html={genAnswerResultsTitleBlack}
                    headingElement={{ ellipsis: true, size: 'large' }}
                  />
                </Box>
              )}
            </Heading>
          )}
        </Box>
        {isLoading ? <SkeletonLoading /> :
          <SearchResultItems
            searchResults={searchResults}
            categoriesPaths={categoriesPaths}
            term={term}
            displayGeneratedAnswer={displayGeneratedAnswer}
          />
      }
      </Box>
      {!(isLoading || displayGeneratedAnswerLoading) &&
        itemsCount > SEARCH_RESULTS_PAGE_SIZE && (
        <Pagination
          className={css.pagination}
          onChange={(e) => onPageChangeEvent(e.page)}
          currentPage={currentPage}
          totalPages={numPages}
          dataHook={DATA_HOOKS.SEARCH_RESULTS_PAGINATION}
        />
      )}
    </Box>
  );
};
